import { css } from "@emotion/react";
import { Suspense, useCallback, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { DebouncedSearch, Loading } from "@phoenix/components";
import { CanModify } from "@phoenix/components/auth";
import { DatasetLabelFilterButton } from "@phoenix/components/dataset/DatasetLabelFilterButton";

import type { DatasetsPageQuery } from "./__generated__/DatasetsPageQuery.graphql";
import { CreateDatasetButton } from "./CreateDatasetButton";
import { DatasetsTable } from "./DatasetsTable";

const pageWrapperCSS = css`
  padding: 24px 40px;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  font-family: var(--ac-premium-font-family, 'Century Gothic', sans-serif);
`;

const controlsContainerCSS = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  /* Target the search input */
  input, [role="searchbox"] {
    border-radius: 9999px !important;
    padding-left: 36px !important;
    background-color: var(--global-color-surface-container-lowest, #ffffff) !important;
    border: 1px solid var(--global-color-outline-variant, #bcc9c6) !important;
  }

  /* Target buttons (Labels and Create Dataset) */
  button, [role="button"] {
    border-radius: 9999px !important;
    padding: 8px 20px !important;
    font-weight: 500 !important;
    font-size: 14px !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;
    height: auto !important;
  }

  /* Specifically target the Create Dataset primary button to have premium styling */
  button[variant="primary"], button.spectrum-Button--cta, button.spectrum-Button--primary {
    background-color: var(--ac-premium-teal, #00685f) !important;
    color: #ffffff !important;
    border: none !important;

    &:hover {
      background-color: var(--global-color-surface-tint, #006a61) !important;
    }
  }
`;

export function DatasetsPage() {
  return (
    <Suspense fallback={<Loading />}>
      <DatasetsPageContent />
    </Suspense>
  );
}

export function DatasetsPageContent() {
  const [fetchKey, setFetchKey] = useState(0);
  const data = useLazyLoadQuery<DatasetsPageQuery>(
    graphql`
      query DatasetsPageQuery {
        ...DatasetsTable_datasets
      }
    `,
    {},
    {
      fetchKey: fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const onDatasetCreated = useCallback(() => {
    setFetchKey((prev) => prev + 1);
  }, [setFetchKey]);

  const [filter, setFilter] = useState<string>("");
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  return (
    <div css={pageWrapperCSS}>
      {/* Page Header */}
      <div style={{ marginBottom: "24px" }}>
        <h2 style={{
          fontSize: "21px",
          fontWeight: 700,
          lineHeight: "28px",
          letterSpacing: "-0.02em",
          color: "var(--global-text-color-900, #191c1d)",
          margin: "0 0 6px 0"
        }}>
          Datasets &amp; Experiments
        </h2>
        <p style={{
          fontSize: "13px",
          color: "var(--global-color-gray-600, #575e70)",
          margin: 0
        }}>
          Manage your evaluation datasets and experiment runs
        </p>
      </div>

      {/* Filters/Controls */}
      <div css={controlsContainerCSS}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1, minWidth: 0 }}>
          <div style={{ flex: "0 1 320px", minWidth: 0 }}>
            <DebouncedSearch
              aria-label="Search datasets by name"
              onChange={setFilter}
              placeholder="Search datasets by name..."
            />
          </div>
          <DatasetLabelFilterButton
            selectedLabelIds={selectedLabelIds}
            onSelectionChange={setSelectedLabelIds}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
          <CanModify>
            <CreateDatasetButton onDatasetCreated={onDatasetCreated} />
          </CanModify>
        </div>
      </div>

      {/* Dataset Table */}
      <DatasetsTable
        query={data}
        filter={filter}
        labelFilter={selectedLabelIds}
      />
    </div>
  );
}
